<template>
  <hr-list-select
    v-bind="$attrs"
    v-model="selectedUserId"
    :options="filteredUserList"
    :disable="disable"
    clearable
    hide-selected
    fill-input
    use-input
    option-value="user"
    :readonly="readonly"
    :loading="isLoadingUsers"
    :display-value="displayValue"
    options-selected-class="text-primary-variant overflow-hidden-x"
    :label="label"
    @filter="onFilter"
  >
    <template #no-option>
      <hr-list-item
        v-if="!isLoadingUsers && !searchStr"
        :label="inProgram
          ? $t('sessions.modals.callInvite.noUsersAvailableAtProgramClient')
          : $t('sessions.modals.callInvite.noUsersAvailable')"
      />
    </template>
  </hr-list-select>
</template>

<script>
import { callFirebaseFunction } from 'boot/firebase';
import { chain, find, get, head, includes, size } from 'lodash';
import { mapState } from 'pinia';
import { HrListItem, HrListSelect } from 'src/design-system';
import { auth } from 'src/store/auth';
import { network } from 'src/store/network';
import { getClientUsers } from 'src/utils/OfflineAccessUtils';

export default {
  name: 'ClientUsersSelector',

  components: { HrListItem, HrListSelect },

  props: {
    modelValue: {
      type: Object,
      default: null,
    },

    sessionId: {
      type: String,
      required: false,
      default: null,
    },

    skipUsers: {
      type: Array,
      default: () => ([]),
    },

    skipCurrentUser: {
      type: Boolean,
      default: false,
    },

    skipRoles: {
      type: Array,
      default: () => ([]),
    },

    disableUsers: {
      type: Array,
      default: () => ([]),
    },

    client: {
      type: String,
      default: null,
    },

    inProgram: {
      type: Boolean,
      default: false,
    },

    readonly: {
      type: Boolean,
      default: false,
    },

    disable: {
      type: Boolean,
      default: false,
    },

    lazyLoad: {
      type: Boolean,
      default: false,
    },

    allowOther: {
      type: Boolean,
      default: false,
    },

    label: {
      type: String,
      default: null,
    },

  },

  emits: ['update:modelValue', 'new-value'],

  data() {
    return {
      inputInitialized: false,
      isLoadingUsers: false,
      usersList: [],

      searchStr: '',
    };
  },

  computed: {
    ...mapState(auth, ['userDetails', 'currentClient', 'userPrivilegesList']),
    ...mapState(network, ['offlineMode']),

    selectedUserId: {
      get() {
        return get(this.modelValue, 'user', null);
      },
      set(userId) {
        const user = find(this.usersList, { user: userId });
        this.$emit('update:modelValue', user);
      },
    },

    filteredUserList() {
      let list;
      if (!this.searchStr) {
        list = (this.usersList.length === 0 && this.modelValue !== null)
          ? [this.modelValue]
          : this.usersList;
      } else {
        list = this.usersList.filter(
          (user) => user?.userName?.toLowerCase()?.includes(this.searchStr.toLowerCase()),
        );
      }
      // HrListSelect renders opt.label + opt.caption natively (optionLabel is fixed to 'label').
      return list.map((user) => ({
        ...user,
        label: user.userName,
        caption: this.getUserRoleLabel(user),
      }));
    },

    displayValue() {
      return get(this.modelValue, 'userName', this.$t('sessions.modals.callInvite.selectUser'));
    },

    isUsersFetchDisabled() {
      return this.readonly || this.lazyLoad;
    },
  },

  watch: {
    isUsersFetchDisabled: {
      immediate: true,
      async handler(isFetchDisabled) {
        if (isFetchDisabled) {
          this.usersList = this.modelValue === null ? [] : [this.modelValue];
        } else {
          await this.loadClientUsers();
        }
      },
    },
  },

  methods: {
    onFilter(val, update) {
      this.searchStr = val;

      if (!this.lazyLoad || this.inputInitialized) {
        update();
        return;
      }
      update(async () => {
        await this.loadClientUsers();
      });
    },

    async loadClientUsers() {
      let clientData;

      this.isLoadingUsers = true;
      this.inputInitialized = true;

      // The flag has to clear on the failure path too, or a rejected fetch leaves the field
      // spinning for the rest of the dialog's life. The rejection itself still propagates.
      try {
        if (!this.offlineMode) {
          clientData = await callFirebaseFunction('getClientUsers', {
            clientId: this.client || this.currentClient.id,
            clinicId: this.userDetails.clinic,
            sessionId: this.sessionId,
            skipRoles: this.skipRoles,
            includeSelf: !this.skipCurrentUser,
          });
        } else {
          clientData = await getClientUsers({
            clientId: this.client || this.currentClient.id,
            clinicId: this.userDetails.clinic,
            sessionId: this.sessionId,
            skipRoles: this.skipRoles,
            includeSelf: !this.skipCurrentUser,
            userId: this.userDetails.userId,
          });
        }

        this.usersList = chain(clientData.data)
          .reject((userInfo) => (includes(this.skipUsers, userInfo.user)))
          .map((userInfo) => ({ ...userInfo, disable: includes(this.disableUsers, userInfo.user) }))
          .value();
        if (size(this.usersList) === 1) {
          const selectedUser = head(this.usersList);
          this.selectedUserId = get(selectedUser, 'user');
        }
      } finally {
        this.isLoadingUsers = false;
      }
    }, // loadClientUsers()

    getUserRoleLabel(userInfo) {
      return chain(this.userPrivilegesList)
        .find({ value: userInfo.userPrivilege || userInfo.role })
        .get('label', '')
        .value();
    }, // getUserRoleLabel()
  },
};
</script>
